import { RefObject, useState } from 'react'
import moment, { Moment } from 'moment-timezone'

interface UseHiddenDaysReturn {
    hiddenDays: Date[]
    toggleHiddenDays: () => void
    hideDaysWithDate: (date: Moment) => void
    restoreRows: () => void
}

export function useHiddenDays(
    selectedDate: Moment,
    dayPickerRef: RefObject<HTMLDivElement | null>
): UseHiddenDaysReturn {
    const [hiddenDays, setHiddenDays] = useState<Date[]>([])

    function hideRows(dayNumber: number[]): void {
        const container: HTMLDivElement | null = dayPickerRef.current
        if (!container) return

        container.querySelectorAll('.rdp-week').forEach((row: Element): void => {
            if (shouldHideRow(row, dayNumber)) {
                row.classList.add('rdp-week--hidden')
            } else {
                row.classList.remove('rdp-week--hidden')
            }
        })
    }

    function shouldHideRow(row: Element, daysToHide: number[]): boolean {
        return Array.from(row.children).every((cell: Element): boolean =>
            Array.from(cell.children).every((child: Element): boolean => {
                const cellContent: string = child.innerHTML.trim()
                return cellContent === '' || daysToHide.includes(Number(cellContent))
            })
        )
    }

    function restoreRows(): void {
        const container: HTMLDivElement | null = dayPickerRef.current
        if (container) {
            container.querySelectorAll('.rdp-week').forEach((row: Element) => {
                row.classList.remove('rdp-week--hidden')
            })
        }
        setHiddenDays([])
    }

    function hideDaysWithDate(date: Moment): void {
        restoreRows()

        const validDate: Moment = date.clone()
        const endOfMonth: Moment = validDate.clone().endOf('month')
        const startOfWeek: Moment = validDate.clone().startOf('isoWeek')
        const endOfWeek: Moment = validDate.clone().endOf('isoWeek')

        const allDates: Date[] = []
        const current: Moment = validDate.clone().startOf('month')
        while (current.isSameOrBefore(endOfMonth)) {
            allDates.push(current.toDate())
            current.add(1, 'day')
        }

        const filteredDates: Date[] = allDates.filter(
            (d: Date) => !(moment(d).isSameOrAfter(startOfWeek) && moment(d).isSameOrBefore(endOfWeek))
        )

        setHiddenDays(filteredDates)
        hideRows(filteredDates.map((d: Date) => moment(d).date()))
    }

    function toggleHiddenDays(): void {
        if (hiddenDays.length === 0) {
            hideDaysWithDate(selectedDate.clone())
        } else {
            restoreRows()
        }
    }

    return { hiddenDays, toggleHiddenDays, hideDaysWithDate, restoreRows }
}
